import React, { useEffect, useState } from "react"
import { IconButton } from "@mui/material"
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined"
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import { Navigate, useNavigate } from "react-router-dom"
import { UserLogin } from "../services/apiUserServices"
import { useGlobalState } from "../helpers/state"
import { setCookie, getCookie, clearCookies } from "../utils/cookies"
import { getPublicIp } from "../utils/publicIp"
import "../css/Passcode.css"

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"]

const loginErrorMessage = (error) => {
  const status = error?.response?.status

  if (status === 401 || status === 403)
    return "Incorrect PIN. Please try again."
  if (status >= 500) return "Server error. Please try again later."
  if (status) return `Login failed (${status}). Please try again.`
  if (error?.code === "ECONNABORTED")
    return "The server took too long to respond. Please try again."

  return "Can't reach the server. Check your connection and try again."
}

const Passcode = () => {
  const navigate = useNavigate()
  const [usercookies, setUsercookies] = useGlobalState("userCookies")
  const [value, setValue] = useState("")
  const [notice, setNotice] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setUsercookies(!!getCookie("usercode"))
  }, [setUsercookies])

  if (usercookies) return <Navigate to="/main" replace />

  const onClick = (number) => {
    if (value.length < 5) setValue((current) => current + number)
  }

  const deleteDigit = () => setValue((current) => current.slice(0, -1))

  const login = async () => {
    if (!value) return

    try {
      setIsLoading(true)
      setNotice("")

      let ip
      try {
        ip = await getPublicIp()
      } catch (ipError) {
        console.log(ipError)
        setNotice(
          "Unable to verify your network connection. Check your internet and try again."
        )
        return
      }

      const data = await UserLogin({ password: value, ip })
      if (data?.flag === 1) {
        clearCookies()
        setCookie("usercode", String(data.usercode || "").toUpperCase(), 999)
        setCookie("agentname", String(data.name || "").toUpperCase(), 999)
        setCookie("accesstoken", data.accessToken || "", 999)
        setCookie("version", data.version || "", 999)
        setCookie("enable_betting", data.enable_betting || "", 999)
        setUsercookies(true)
        navigate("/main", { replace: true })
      } else if (data?.flag === 2) {
        setNotice(
          data?.message ||
            "This PIN is already logged in on another device today."
        )
      } else {
        setNotice("Incorrect PIN. Please try again.")
      }
    } catch (error) {
      console.log(error)
      setNotice(loginErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter") login()
    if (event.key === "Backspace") deleteDigit()
  }

  return (
    <main className="passcode-page">
      <section className="passcode-shell">
        <div className="passcode-card" onKeyDown={handleKeyDown}>
          {/* <div className="passcode-lock" aria-hidden="true">
            <LockOutlinedIcon />
          </div> */}
          <h1>Enter your PIN</h1>
          {/* <p>Use PIN / Touch ID</p> */}
          {notice && (
            <div className="passcode-notice" role="alert">
              <span>{notice}</span>
              <button
                type="button"
                onClick={() => setNotice("")}
                aria-label="Dismiss notice"
              >
                ×
              </button>
            </div>
          )}
          <div
            className="passcode-display"
            aria-label={`Entered passcode: ${value || "none"}`}
          >
            <span className="passcode-entered">{value}</span>
            <span className="passcode-empty" aria-hidden="true">
              {"○".repeat(5 - value.length)}
            </span>
          </div>
          <div className="passcode-keypad">
            {KEYS.map((key) => (
              <IconButton
                className="passcode-key"
                key={key}
                onClick={() => onClick(key)}
                aria-label={`Enter ${key}`}
              >
                {key}
              </IconButton>
            ))}
            <IconButton
              className="passcode-key"
              onClick={login}
              aria-label="Log in"
              disabled={!value || isLoading}
            >
              {isLoading ? (
                <span className="passcode-spinner" aria-label="Logging in" />
              ) : (
                <LoginOutlinedIcon />
              )}
            </IconButton>
            <IconButton
              className="passcode-key"
              onClick={() => onClick("0")}
              aria-label="Enter 0"
            >
              0
            </IconButton>
            <IconButton
              className="passcode-key"
              onClick={deleteDigit}
              aria-label="Delete last digit"
            >
              <BackspaceOutlinedIcon />
            </IconButton>
          </div>
          {/* <button className="passcode-forgot" type="button" onClick={() => setValue("")}>Forgot PIN?</button> */}
        </div>
      </section>
    </main>
  )
}

export default Passcode
